import type { NotificationKind } from '@/lib/api/generated/model';

/**
 * The ten types the notifications page filters by (spec 147 Q8; spec 159
 * added Your accounts, spec 187 Workspace members), in the order its Type
 * menu lists them. People think
 * in these, not in the service's twenty kinds.
 */
export const TYPE_GROUPS = [
  { id: 'gates', label: 'Waiting at a gate' },
  { id: 'runs', label: 'Runs' },
  { id: 'subtasks', label: 'Sub-tasks' },
  { id: 'comments', label: 'Comments and mentions' },
  { id: 'done', label: 'Done' },
  { id: 'spend', label: 'Spend' },
  { id: 'triggers', label: 'Triggers and connections' },
  { id: 'accounts', label: 'Your accounts' },
  { id: 'members', label: 'Workspace members' },
  { id: 'escalations', label: 'Escalations' },
] as const;

export type TypeGroup = (typeof TYPE_GROUPS)[number]['id'];

export const TYPE_GROUP_IDS: readonly TypeGroup[] = TYPE_GROUPS.map(
  (group) => group.id,
);

/**
 * Each kind's type. Exhaustive over `NotificationKind`, beside `KIND_ICON`,
 * so a new kind cannot ship without a place in the Type filter.
 */
export const KIND_TYPE_GROUP: Record<NotificationKind, TypeGroup> = {
  gate_reached: 'gates',
  gate_assigned: 'gates',
  task_failed: 'runs',
  task_blocked: 'runs',
  task_stopped: 'runs',
  child_needs_attention: 'subtasks',
  children_finished: 'subtasks',
  linked_tasks_waiting: 'subtasks',
  scheduler_failed: 'subtasks',
  scheduler_stalled: 'subtasks',
  task_commented: 'comments',
  task_mentioned: 'comments',
  // An agent's answer on a run's conversation (spec 149).
  run_replied: 'comments',
  task_done: 'done',
  spend_limit_reached: 'spend',
  spend_limit_approaching: 'spend',
  trigger_failed: 'triggers',
  connection_failed: 'triggers',
  escalated: 'escalations',
  // About the person, not a workspace's triggers (spec 159 US2).
  forge_account_broken: 'accounts',
  forge_account_expiring: 'accounts',
  forge_account_revoked: 'accounts',
  forge_account_required: 'accounts',
  forge_account_requested: 'accounts',
  // Someone joined or left a workspace (spec 187).
  workspace_member_added: 'members',
  workspace_member_removed: 'members',
  workspace_access_requested: 'members',
  workspace_without_owner: 'members',
  workspace_members_review: 'members',
};

/** The kinds the service is asked for when these types are picked, sorted. */
export function kindsOf(groups: readonly TypeGroup[]): NotificationKind[] {
  const wanted = new Set(groups);
  return (Object.keys(KIND_TYPE_GROUP) as NotificationKind[])
    .filter((kind) => wanted.has(KIND_TYPE_GROUP[kind]))
    .sort();
}

export function typeGroupLabel(id: TypeGroup): string {
  return TYPE_GROUPS.find((group) => group.id === id)?.label ?? id;
}
